import { type InferResponseType, unwrap } from "@nexxt/api-client";
import type { ListLinesheetInvitationsInput } from "@nexxt/contracts/catalog";
import { infiniteQueryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the invitations to the brands (catalog.md §2.4): the brands that joined a campaign, by page.

const invitations = api.catalog["linesheet-invitations"];

type ListPage = InferResponseType<typeof invitations.$get, 200>;
export type LinesheetInvitation = ListPage["items"][number];

/** The filters of the list input (`listLinesheetInvitationsInput`), without the paging the query adds. */
export type InvitationFilters = Pick<ListLinesheetInvitationsInput, "season" | "salesCampaignId"> & {
  /** Still waited for after the last reminder (B-97, ADR-245): Operations' queue. */
  remindersOver?: boolean | undefined;
};

const PAGE_SIZE = 50;

const keys = { all: ["linesheet-invitations"] as const };

export const invitationQueries = {
  ...keys,

  list: ({ season, salesCampaignId, remindersOver }: InvitationFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.all, "list", { season, salesCampaignId, remindersOver }],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          invitations.$get(
            {
              query: {
                ...(season && { season }),
                ...(salesCampaignId && { salesCampaignId }),
                ...(remindersOver && { remindersOver: "true" }),
                page: String(pageParam),
                pageSize: String(PAGE_SIZE),
              },
            },
            { init: { signal } },
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),
};

/** An invitation by what tells it from the others: the brand and its campaign. */
export const invitationName = (invitation: Pick<LinesheetInvitation, "brand" | "salesCampaign">) =>
  `${invitation.brand} · ${invitation.salesCampaign.name}`;
